import { nextTick } from "vue";

/**
 * 페이지 안 이동 스크롤 공용 도우미(74차 UXV74-07 · 12).
 * - JS 로 준 `behavior: "smooth"` 는 전역 CSS 의 동작 줄이기 규칙을 따르지 않는다. 화면마다 `matchMedia` 를 다시 쓰던 것(6곳)과
 *   쓰지 않던 것(51곳)을 이 도우미 하나로 맞춘다 — 동작 줄이기를 켠 사용자는 즉시 이동한다.
 * - 상단바 · 붙은 구획 줄 · 하단 동작 줄을 비켜 서는 여백은 `html` 의 `scroll-padding`(main.scss 74차 블록)이 맡는다. 그래서
 *   `scrollIntoView` 는 오프셋을 따로 계산하지 않고, 화면은 대상에 `scroll-margin-top` 을 두지 않는다(두면 여백이 두 번 들어간다).
 * - `window.scrollTo({ top })` 은 scroll-padding 을 따르지 않는다. 좌표를 직접 계산하는 곳(SectionStepper)은 자기 오프셋을 쓰고
 *   `behavior` 만 `scrollBehavior()` 로 받는다.
 */

/** 동작 줄이기 설정이 켜져 있다 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** 이동 스크롤 방식 — 동작 줄이기면 즉시(auto), 아니면 부드럽게(smooth) */
export function scrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}

/**
 * 요소가 보이도록 문서를 스크롤한다(기본: 요소 머리를 붙은 줄 바로 아래에). 요소가 없으면 아무것도 하지 않는다.
 * `focus` 면 이동 뒤 그 요소로 포커스를 옮긴다(스크롤은 다시 하지 않는다). 포커스를 받을 수 없는 요소면 `tabindex="-1"` 을 붙인다 —
 * Tab 순서에는 들지 않는다. 붙인 `tabindex` 는 그 요소가 포커스를 잃을 때 한 번 뗀다(75차 UXS75-18 ② — 남겨 두면 카드 안 빈 곳을
 * 누를 때마다 카드 전체가 포커스를 받아, 이후 Tab 이 그 카드 다음부터 시작했다). 원래 있던 tabindex 는 건드리지 않는다.
 */
export function scrollToBlock(
  el: Element | null | undefined,
  options?: {
    /** 세로 맞춤(기본 start) */
    block?: ScrollLogicalPosition;
    /** 가로 맞춤(기본 nearest — 가로 스크롤 상자를 흔들지 않는다) */
    inline?: ScrollLogicalPosition;
    /** 이동 뒤 그 요소에 포커스 */
    focus?: boolean;
  },
): void {
  if (!el) return;
  el.scrollIntoView({
    behavior: scrollBehavior(),
    block: options?.block ?? "start",
    inline: options?.inline ?? "nearest",
  });
  if (!options?.focus || !(el instanceof HTMLElement)) return;
  if (el.tabIndex < 0 && !el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
    el.addEventListener(
      "blur",
      () => {
        el.removeAttribute("tabindex");
      },
      { once: true },
    );
  }
  el.focus({ preventScroll: true });
}

const SECTION_HEADINGS = "h1, h2, h3, h4";

/** `scrollend` 가 오지 않을 때(이미 제자리라 스크롤이 없었다) 판정하러 가는 상한 */
const REALIGN_MAX_WAIT_MS = 900;
/** `scrollend` 를 모르는 브라우저에서 smooth 스크롤이 끝나기를 기다리는 시간 */
const REALIGN_SMOOTH_WAIT_MS = 600;

/** 화면에 실제로 그려진 요소인지 — `display:none` 조상(v-show 숨은 보기) 안이면 상자가 없다 */
function isRendered(el: HTMLElement): boolean {
  return (
    el.getClientRects().length > 0 &&
    getComputedStyle(el).visibility !== "hidden"
  );
}

/**
 * 「…로 이동」 버튼 전용 도우미(80차 JR80-05) — 구획으로 옮기되 **제목에 포커스**한다. 누른 버튼이 화면 밖으로 밀려도 키보드 · 화면 읽기
 * 사용자가 도착한 자리에서 이어 읽는다. `SectionStepper` 의 「가까운 구획 제목으로 포커스」 와 같은 규칙이다.
 * - `section` 이 제목(h1 ~ h4)이면 그것, 아니면 그 안의 **보이는** 첫 제목(`SectionHead` 의 h2 · h3), 제목이 없으면 구획 자체로 간다.
 * - 크기 0 인 숨은 앵커(`aria-hidden` 빈 span)면 그 앵커를 품은 가까운 `section` 으로 바꿔 찾는다 — 숨은 요소에는 포커스가 가지 않는다.
 * - 스크롤은 `scrollToBlock` 이라 상단바 · 붙은 줄 여백(`scroll-padding`)과 동작 줄이기 설정을 그대로 따른다. 대상이 없으면 아무것도 하지 않는다.
 * - `realign`(기본 true, 89차 C89-01 · JR89-03): 스크롤이 끝난 뒤(`scrollend` · 없으면 2프레임 · 동작 줄이기가 아니면 짧은 대기) 대상 제목이 붙은 띠
 *   (`scroll-padding-top`) 밑이거나 화면 밖이면 `scrollIntoView({ block: "start", behavior: "auto" })` 를 한 번 더 한다(포커스 불변). 이동하는 사이
 *   로딩 표시 · 단계 표시가 걷히며 본문이 움직여도 도착 제목이 가려지지 않는다(WCAG 2.4.11). 어긋나지 않았으면 움직이지 않는다 — 어긋남의 **원인**
 *   (로딩 줄을 걷기 전에 이동)은 호출 화면이 `await nextTick()` 뒤에 부르는 것으로 고친다.
 */
export function jumpToSection(
  section: Element | null | undefined,
  options?: { realign?: boolean },
): void {
  if (!section) return;
  let anchor = section;
  if (
    anchor instanceof HTMLElement &&
    (anchor.getAttribute("aria-hidden") === "true" ||
      (anchor.offsetWidth === 0 && anchor.offsetHeight === 0))
  ) {
    anchor = anchor.closest("section") ?? anchor.parentElement ?? anchor;
  }
  const heading = anchor.matches(SECTION_HEADINGS)
    ? anchor
    : [...anchor.querySelectorAll<HTMLElement>(SECTION_HEADINGS)].find(
        isRendered,
      );
  const target = heading ?? anchor;
  scrollToBlock(target, { focus: true });
  if (options?.realign ?? true) realignAfterScroll(target);
}

/** 사용자가 직접 스크롤하려는 입력 — 휠 · 터치 · 포인터 누름 · 스크롤 키. 포커스를 옮기지 않아 포커스 검사로는 알 수 없다 */
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown"] as const;
const USER_SCROLL_KEYS = new Set([
  "PageUp",
  "PageDown",
  "Home",
  "End",
  "ArrowUp",
  "ArrowDown",
  " ",
]);

/**
 * 스크롤이 끝난 뒤 한 번 부른다 — `scrollend` 가 있으면 그것(안 오면 상한 시간), 없으면 2프레임(smooth 면 짧은 대기).
 * 기다리는 동안 사용자가 직접 스크롤하는 입력(휠 · 터치 · 포인터 · 스크롤 키)이 한 번이라도 있으면 부르지 않는다(RVDS89-01 — 떠난 화면을 제목으로 되돌리지 않는다).
 */
function afterScrollSettles(callback: () => void): void {
  let done = false;
  let timer = 0;
  const finish = (): void => {
    done = true;
    window.clearTimeout(timer);
    window.removeEventListener("scrollend", run);
    for (const name of USER_SCROLL_EVENTS) {
      window.removeEventListener(name, cancel, true);
    }
    window.removeEventListener("keydown", onKeydown, true);
  };
  function run(): void {
    if (done) return;
    finish();
    callback();
  }
  function cancel(): void {
    if (!done) finish();
  }
  function onKeydown(event: KeyboardEvent): void {
    if (USER_SCROLL_KEYS.has(event.key)) cancel();
  }
  for (const name of USER_SCROLL_EVENTS) {
    window.addEventListener(name, cancel, { capture: true, passive: true });
  }
  window.addEventListener("keydown", onKeydown, true);
  if (Reflect.has(window, "onscrollend")) {
    window.addEventListener("scrollend", run, { once: true });
    timer = window.setTimeout(run, REALIGN_MAX_WAIT_MS);
    return;
  }
  if (prefersReducedMotion()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    return;
  }
  timer = window.setTimeout(run, REALIGN_SMOOTH_WAIT_MS);
}

/** 포커스한 제목이 붙은 띠 밑이거나 화면 밖이면 한 번 더 맞춘다(`jumpToSection` 의 `realign`) */
function realignAfterScroll(target: Element): void {
  afterScrollSettles(() => {
    if (!target.isConnected) return;
    // 기다리는 사이 사용자가 다른 곳으로 포커스를 옮겼으면 그 뜻을 따른다
    const active = document.activeElement;
    if (active && active !== document.body && active !== target) return;
    const rect = target.getBoundingClientRect();
    const { top, bottom } = stickyInsets();
    const offscreen =
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight - bottom ||
      rect.top < top - 1;
    if (!offscreen) return;
    target.scrollIntoView({ behavior: "auto", block: "start" });
  });
}

// ---- 상태 전환 포커스(81차 an-journey C1 · C2) -------------------------------------------------------------------------
// 확정 · 완료 · 삭제 · 생성으로 누른 버튼이 `v-if` 로 사라지면 포커스가 body 로 떨어진다(72차 리뷰 HIGH 3건, 81차 감사 20곳 넘게).
// 같은 처리부가 「다음 포커스 대상」 을 이 두 도우미로 정한다. 같은 버튼 글만 바꿀 수 있으면 그쪽(버튼이 사라지지 않게)을 먼저 쓴다.

/** 포커스 대상 지정 — 요소 · Vue ref(요소 또는 컴포넌트) · 셀렉터 · 요소를 돌려주는 함수 */
export type FocusTarget =
  | Element
  | null
  | string
  | undefined
  | { value: unknown }
  | (() => FocusTarget);

/** 대상 지정을 요소로 푼다(컴포넌트 ref 는 `$el`) */
function resolveTarget(target: FocusTarget, depth = 0): HTMLElement | null {
  if (depth > 3 || target === null || target === undefined) return null;
  if (typeof target === "string") {
    return document.querySelector<HTMLElement>(target);
  }
  if (typeof target === "function") return resolveTarget(target(), depth + 1);
  if (target instanceof HTMLElement) return target;
  if (target instanceof Element) return null;
  const value = target.value;
  if (value instanceof HTMLElement) return value;
  if (value && typeof value === "object" && "$el" in value) {
    const el = value.$el;
    return el instanceof HTMLElement ? el : null;
  }
  return null;
}

/** 열린 모달 대화상자(`aria-modal`) — 그 밖 요소로는 포커스를 보내지 않는다(배경 본문으로 새는 것 방지, R-DS-L2) */
function openModal(leaving?: Element | null): Element | null {
  for (const modal of document.querySelectorAll('[aria-modal="true"]')) {
    if (modal === leaving) continue;
    if (modal instanceof HTMLElement && isRendered(modal)) return modal;
  }
  return null;
}

/**
 * 지금 포커스할 수 있는 대상인가 — 연결 · 그려짐 · `:disabled` 가 아니고, `inert` · `aria-hidden` 조상 안이 아니며(접힌 `StickyActionBar` 같은 곳은
 * `focus()` 가 조용히 실패한다), 열린 모달이 있으면 그 안이다
 * (`leaving` = 지금 닫히는 중이라 아직 DOM 에 남은 모달 — 빼고 센다).
 */
function isFocusable(
  el: HTMLElement | null,
  leaving?: Element | null,
): el is HTMLElement {
  if (!el?.isConnected || !isRendered(el)) return false;
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.closest('[inert], [aria-hidden="true"]')) return false;
  const modal = openModal(leaving);
  return !modal || modal.contains(el);
}

/** `scroll-padding-top` · `scroll-padding-bottom` 해석값(px) — 상단바 + 붙은 구획 줄 · 하단 동작 줄. 해석 못 하면 0 */
function stickyInsets(): { top: number; bottom: number } {
  const style = getComputedStyle(document.documentElement);
  const read = (value: string): number => {
    const px = Number.parseFloat(value);
    return Number.isFinite(px) ? px : 0;
  };
  return {
    top: read(style.scrollPaddingTop),
    bottom: read(style.scrollPaddingBottom),
  };
}

/** 대상이 붙은 띠 밑이거나 화면 밖이다 */
function hiddenBySticky(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const { top, bottom } = stickyInsets();
  return rect.top < top || rect.bottom > window.innerHeight - bottom;
}

/** 붙인 `tabindex="-1"` 은 포커스를 잃을 때 한 번 뗀다(`scrollToBlock` 과 같은 규칙) */
function ensureFocusable(el: HTMLElement): void {
  if (el.tabIndex >= 0 || el.hasAttribute("tabindex")) return;
  el.setAttribute("tabindex", "-1");
  el.addEventListener(
    "blur",
    () => {
      el.removeAttribute("tabindex");
    },
    {
      once: true,
    },
  );
}

/** 붙였던 `tabindex="-1"` 을 되돌린다(포커스가 실제로 오지 않았을 때 blur 가 안 와서 남지 않게) */
function releaseTabindex(el: HTMLElement, hadTabindex: boolean): void {
  if (!hadTabindex) el.removeAttribute("tabindex");
}

/**
 * 대상에 포커스를 시도한다 — 실제로 `activeElement` 가 됐으면 true. 안 됐으면(조용히 실패) 붙인 `tabindex` 를 되돌리고 false 라 호출부가 다음 후보로 간다.
 */
function tryFocus(
  el: HTMLElement | null,
  leaving?: Element | null,
): el is HTMLElement {
  if (!isFocusable(el, leaving)) return false;
  const hadTabindex = el.hasAttribute("tabindex") || el.tabIndex >= 0;
  ensureFocusable(el);
  el.focus({ preventScroll: true });
  if (document.activeElement === el) return true;
  releaseTabindex(el, hadTabindex);
  return false;
}

/**
 * 포커스한 대상이 붙은 띠 밑이거나 화면 밖이면 보이게 한다(이미 보이면 가만히). 대상이 보이는 띠보다 **크면**(구획 전체가 fallback 인 경우) 가운데 맞춤은 머리를
 * 화면 위로 밀어내므로 `start`(scroll-padding 이 상단바 · 붙은 줄을 비켜 준다) — 낭독 시작점이 제목이다(R-DS-M3).
 */
function revealFocused(el: HTMLElement): void {
  if (!hiddenBySticky(el)) return;
  const { top, bottom } = stickyInsets();
  const band = window.innerHeight - top - bottom;
  scrollToBlock(el, {
    block: el.getBoundingClientRect().height > band ? "start" : "center",
  });
}

/**
 * 상태 전환 뒤 포커스(C1) — 확정 · 완료 · 생성 · 시작 · 결정 · 발송으로 눌렀던 버튼이 사라진 자리에서 **다음 대상**(구획 제목 · 새 버튼 · 결과 카드)으로 포커스를 옮긴다.
 * - `await nextTick()` 뒤에 대상을 푼다(방금 `v-if` 로 나타난 요소도 잡힌다). 대상이 없거나 그려지지 않았거나 잠겼으면 `fallback`(보통 구획 제목)으로 간다. 둘 다 없으면 아무것도 하지 않는다(`false`).
 * - 포커스할 수 없는 요소(제목 · 카드)에는 `tabindex="-1"` 을 **없을 때만** 붙이고 포커스를 잃을 때 뗀다. 포커스는 `preventScroll` 로 옮긴다.
 * - `inert` · `aria-hidden` 조상 안이거나 열린 모달 밖이라 포커스가 실제로 오지 않으면 `fallback` 으로 넘긴다(그래도 안 되면 `false`, 붙인 `tabindex` 는 되돌린다).
 * - 옮긴 뒤 대상이 화면 밖이거나 붙은 띠(상단바 + `SectionStepper` — `scroll-padding-top` 과 같은 값 · 하단 동작 줄 `scroll-padding-bottom`) 밑이면 `scrollToBlock` 으로 보이게 한다.
 *   이미 보이면 스크롤하지 않는다(화면이 흔들리지 않는다).
 * 사용 예: `await confirm(); await focusAfterSwap(() => resultCardRef, { fallback: "#core-confirm-heading" });`
 * 반환: 포커스를 옮겼으면 true.
 */
export async function focusAfterSwap(
  target: FocusTarget,
  options?: { fallback?: FocusTarget },
): Promise<boolean> {
  await nextTick();
  // 포커스가 실제로 오지 않으면(`inert` 조상 · 모달 밖 등) 다음 후보로 간다.
  const el = [resolveTarget(target), resolveTarget(options?.fallback)].find(
    (candidate): candidate is HTMLElement => tryFocus(candidate),
  );
  if (!el) return false;
  revealFocused(el);
  return true;
}

/**
 * 닫히는 층(드로어 · 모달)이 포커스를 돌려줄 곳 찾기(C93-11) — `targets` 를 앞에서부터 풀어 **처음 포커스되는 요소**로 옮긴다. 동기다.
 * - `leaving` = 닫히는 중이라 leave 전환 동안 DOM 에 남아 있는 층. `focusAfterSwap` 은 열린 모달 밖으로 포커스를 보내지 않는데 이 층이 아직
 *   `aria-modal` 이라 막히므로, 이 층만 빼고 센다(다른 모달이 위에 떠 있으면 그대로 막는다).
 * - 제목 같은 요소에는 `tabindex="-1"` 을 없을 때만 붙이고 포커스를 잃을 때 뗀다. 옮긴 뒤 붙은 띠 밑이면 보이게 한다. 옮겼으면 true.
 */
export function focusReturnTarget(
  targets: FocusTarget[],
  leaving?: Element | null,
): boolean {
  for (const target of targets) {
    const el = resolveTarget(target);
    if (tryFocus(el, leaving)) {
      revealFocused(el);
      return true;
    }
  }
  return false;
}

/**
 * 표 행 삭제 뒤 포커스(C2, JR81-03) — 지운 행의 **다음 행 같은 열 버튼 → 이전 행 → 표 아래 「추가」 버튼** 순으로 포커스한다.
 * - `rowIndex` 는 지우기 **전** 행의 순번(0 부터, `tbody > tr`). 지운 뒤 같은 순번이 곧 다음 행이다. 호출은 데이터를 지운 직후(이 함수가 `nextTick` 을 기다린다).
 * - `columnSelector` 는 행 안에서 그 열의 누를 것(「삭제」 · 「수정」 버튼, 예 `.col-actions button`). 잠긴(`disabled`) 버튼은 건너뛴다.
 * - 행이 하나도 남지 않으면 `options.addButton`(요소 · 셀렉터), 없으면 표 뒤쪽 가까운 구획(`.card` · `section`)의 글에 「추가」 가 든 첫 버튼, 그것도 없으면 `options.fallback`
 *   · 표 자체(`tabindex="-1"`)로 간다.
 * - 93차 C93-15(JR93-04): `options.rowSelector` — 행이 `tbody > tr` 이 아닌 목록(`ul > li` · 카드 줄)이면 그 셀렉터(`tableEl` 안에서 찾는다). 기본 `"tbody > tr"` 라 기존 호출은 그대로.
 * 사용 예: `const i = rows.indexOf(row); rows.splice(i, 1); await focusAfterRowRemoval(tableEl, i, ".row-actions button");`
 *   목록: `await focusAfterRowRemoval(listEl, i, "button.remove", { rowSelector: "ul > li", addButton: ".add" });`
 */
export async function focusAfterRowRemoval(
  tableEl: FocusTarget,
  rowIndex: number,
  columnSelector: string,
  options?: {
    addButton?: FocusTarget;
    fallback?: FocusTarget;
    rowSelector?: string;
  },
): Promise<boolean> {
  await nextTick();
  const table = resolveTarget(tableEl);
  if (!table) {
    return focusAfterSwap(options?.addButton, { fallback: options?.fallback });
  }
  const rows = [
    ...table.querySelectorAll<HTMLElement>(
      options?.rowSelector ?? "tbody > tr",
    ),
  ];
  const buttonOf = (row: HTMLElement | undefined): HTMLElement | null => {
    const found = row?.querySelector<HTMLElement>(columnSelector) ?? null;
    return isFocusable(found) ? found : null;
  };
  const next = buttonOf(rows[rowIndex]);
  const previous = buttonOf(rows[rowIndex - 1]);
  const target = next ?? previous;
  if (target && tryFocus(target)) {
    revealFocused(target);
    return true;
  }
  const add =
    resolveTarget(options?.addButton) ??
    [
      ...(table
        .closest<HTMLElement>(".card, section, form")
        ?.querySelectorAll<HTMLElement>("button") ?? []),
    ].find(
      (button) =>
        !table.contains(button) &&
        isFocusable(button) &&
        button.textContent.includes("추가") &&
        !!(
          table.compareDocumentPosition(button) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ),
    ) ??
    null;
  return focusAfterSwap(add, { fallback: options?.fallback ?? table });
}
